import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, unwrap, type Me, type Permission, type Session } from "@/api/client";
import { getAccessToken, onAccessTokenChange, refreshSession, setAccessToken } from "@/api/session";
import { useI18n } from "@/i18n";

type Status = "loading" | "anonymous" | "authenticated";

type AuthValue = {
  status: Status;
  me: Me | undefined;
  /** Adopt a session returned by login, verify, accept-invite, workspace create/switch. */
  signIn: (session: Session) => Promise<void>;
  signOut: () => Promise<void>;
  refetchMe: () => Promise<unknown>;
  can: (...perms: Permission[]) => boolean;
};

const AuthContext = createContext<AuthValue | null>(null);

export const meQueryKey = ["me"] as const;

/** One full pass of the five-dot pulse (styles.css, .z-mark-pulse). */
const BRAND_LOADER_MS = 1100;

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { locale, setLocale } = useI18n();
  const [token, setToken] = useState<string | null>(getAccessToken());
  const [booted, setBooted] = useState(false);

  // A reload has no access token in memory; the refresh cookie restores it.
  // The opening screen shows the pulsing mark for at least one full sequence
  // (Design Brief §7: loading screens are a brand moment). Restoring a session
  // takes well under 100 ms, which would otherwise make it a flicker. Only the
  // first load waits; navigation inside the app never does, and users who ask
  // for reduced motion are not held at all.
  useEffect(() => {
    const off = onAccessTokenChange(setToken);
    const started = performance.now();
    const minimum = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : BRAND_LOADER_MS;
    let timer: number | undefined;
    void refreshSession().finally(() => {
      const wait = Math.max(0, minimum - (performance.now() - started));
      timer = window.setTimeout(() => setBooted(true), wait);
    });
    return () => {
      off();
      window.clearTimeout(timer);
    };
  }, []);

  const meQuery = useQuery({
    queryKey: meQueryKey,
    queryFn: () => unwrap(api.GET("/v1/me")),
    enabled: token !== null,
    staleTime: 60_000,
    retry: false,
  });

  // The user's saved language wins once, on load. After that the switcher writes
  // through to the server, so the two never disagree for long.
  const syncedLocale = useRef(false);
  useEffect(() => {
    const userLocale = meQuery.data?.user.locale;
    if (userLocale && !syncedLocale.current) {
      syncedLocale.current = true;
      if (userLocale !== locale) setLocale(userLocale);
    }
  }, [meQuery.data, locale, setLocale]);

  const signIn = useCallback(
    async (session: Session) => {
      setAccessToken(session.accessToken);
      await queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    try {
      await fetch(`${import.meta.env.VITE_API_BASE_URL ?? ""}/v1/auth/logout`, { method: "POST", credentials: "include" });
    } finally {
      setAccessToken(null);
      syncedLocale.current = false;
      queryClient.clear();
    }
  }, [queryClient]);

  const value = useMemo<AuthValue>(() => {
    let status: Status = "loading";
    if (!booted) status = "loading";
    else if (token === null) status = "anonymous";
    else if (meQuery.data) status = "authenticated";
    else if (meQuery.isError) status = "anonymous";
    const perms = new Set(meQuery.data?.permissions ?? []);
    return {
      status,
      me: meQuery.data,
      signIn,
      signOut,
      refetchMe: meQuery.refetch,
      can: (...p) => p.some((x) => perms.has(x)),
    };
  }, [booted, token, meQuery.data, meQuery.isError, meQuery.refetch, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
